import React, { useCallback, useContext, useRef, useLayoutEffect, useState } from 'react';
import { ChooseTreatmentContext } from '../FreeDosageLayoutV2';
import { useTabSwipe } from '../../../../customHooks/useTabSwipe';
import ZipcodeCheck from './ZipcodeCheck';
import * as constants from "../constants"
import * as translator from '../../../../utils/translator';
import { MdInfoOutline } from "react-icons/md";
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { useCartContext } from '../../../../customHooks/useCart';
import { useCustomer } from '../../../../customHooks/useCustomer';
import SparkleLabel from './SparkleLabel';
import ToggleRadioButton from '../../../../components/ToggleRadioButton/ToggleRadioButton';
import FastDoctorReview from '../../../../components/FastDoctorReview/FastDoctorReview';
import './ShippingAndServiceSelection.css';
import { SORT_BY_PRICE_DESC, SORT_BY_PRICE_ASC, SortByType } from '../../types';
import { SHIPPING_EVENTS, buildToggleClickProps, buildEligibleCheckProps } from '../../../v2/ShippingV2/utils/shippingTrackingEvents';

const RadioUnselected: React.FC = () => (
  <div className="flex-shrink-0 rounded-full bg-white radio-unselected" />
);

const RadioSelected: React.FC = () => (
  <div className="flex-shrink-0 rounded-full flex items-center justify-center radio-selected">
    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const CheckmarkBullet: React.FC = () => (
  <span className="flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full checkmark-bullet-circle">
    <svg width="8" height="7" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 5L4.5 8.5L11 1.5" stroke="#037D73" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const ShippingAndServiceDropdowns: React.FC = () => {
  const chooseTreatmentContext = useContext(ChooseTreatmentContext);
  const { getCart } = useCartContext();
  const { customerState } = useCustomer();
  const [previousShippingMethodSelected, setPreviousShippingMethodSelected] = useState<string>(constants.SHIPPING);

  const deliveryBtnRef = useRef<HTMLButtonElement>(null);
  const pickupBtnRef = useRef<HTMLButtonElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const {
    language,
    shippingMethod,
    setShippingMethod,
    selectedService,
    setSelectedService,
    setSelectedApprovalService,
    setNearbyPharmacies,
    setPurchaseTierFilter,
    setSelectedStockCode,
    setIsExpressPrescriptionReview,
    isExpressPrescriptionReview,
    isExpressUberFlow,
    isExpressUrbifyFlow,
    customerZipcode,
    setCustomerZipcode,
    expressStockOptions,
    setExpressStockOptions,
    stockCodesInRange,
    setStockCodesInRange,
    isFastApprovalFlow,
    onExpressLabelClick,
    resetMethodSelection,
    resetExpressTierCarts,
    trackSelfPickupView,
    customerAddresses,
    parseProducts,
    setProducts,
    setSelectedExpressTier,
  } = chooseTreatmentContext || {} as any;

  const updateTabsPill = useCallback(() => {
    if (isDraggingRef.current) return;

    const activeBtn =
      shippingMethod === constants.PICKUP
        ? pickupBtnRef.current
        : deliveryBtnRef.current;

    const pill = pillRef.current;

    if (!activeBtn || !pill) return;

    pill.style.width = `${activeBtn.getBoundingClientRect().width}px`;
    pill.style.transform = `translateX(${activeBtn.offsetLeft - 4}px)`;
  }, [shippingMethod]);

  useLayoutEffect(() => {
    const animationFrameId = requestAnimationFrame(updateTabsPill);

    const deliveryBtn = deliveryBtnRef.current;
    const pickupBtn = pickupBtnRef.current;

    if (!deliveryBtn || !pickupBtn) {
      return () => cancelAnimationFrame(animationFrameId);
    }

    const resizeObserver = new ResizeObserver(() => {
      updateTabsPill();
    });

    resizeObserver.observe(deliveryBtn);
    resizeObserver.observe(pickupBtn);

    window.addEventListener('resize', updateTabsPill);

    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTabsPill);
    };
  }, [updateTabsPill, language]);

  const trackShippingMethodSelected = (newTab: string) => {
    const cart = getCart();
    const cartEmpty = !cart?.products || cart.products.length === 0 ? 'yes' : 'no';
    const userId = customerState?.customerID || null;

    MainTracker.track("custom", "Shipping_method_selected", {
      user_id: userId,
      previous_tab: previousShippingMethodSelected || 'N/A',
      new_tab: newTab,
      cart_empty: cartEmpty
    });
  };

  const switchToDelivery = () => {
    if (shippingMethod !== constants.PICKUP) return;

    resetMethodSelection();
    setPreviousShippingMethodSelected(shippingMethod);
    trackShippingMethodSelected(constants.SHIPPING);
    setPurchaseTierFilter('premium');
    setSelectedService(constants.SERVICE_PRESCRIPTION_AND_MEDICATION);
    setShippingMethod(constants.SHIPPING);
    setIsExpressPrescriptionReview(false);
    setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
  };

  const switchToPickup = () => {
    if (shippingMethod === constants.PICKUP) return;

    resetMethodSelection();
    setPreviousShippingMethodSelected(shippingMethod);
    trackShippingMethodSelected(constants.PICKUP);
    trackSelfPickupView();
    setShippingMethod(constants.PICKUP);
    setSelectedService(constants.SERVICE_PRESCRIPTION_AND_MEDICATION);
    setPurchaseTierFilter('premium');
    setIsExpressPrescriptionReview(false);
    setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
  };

  // Handle address selection (address+ZIP search)
  const handleAddressSelected = useCallback((
    address: any,
    expressStocks: string[] = [],
    expressStockOptions: any[] = []
  ) => {
    setCustomerZipcode(address.zip);
    setStockCodesInRange(expressStocks);
    setExpressStockOptions(expressStockOptions);

    // Reparse products immediately with the new ZIP's full-union stock codes.
    // Without this, products keep stale express_stock flags from the previous parse,
    // while stockCodesInRange already has the new codes. minPriceForProductExpress
    // then finds no matching available_stocks and returns 0.
    setProducts((current: any[]) => parseProducts(current, true, expressStocks));
    setSelectedExpressTier?.(null);

    const allTiers = (expressStockOptions ?? []).flatMap((opt: any) => opt.tiers ?? []);
    MainTracker.track('custom', SHIPPING_EVENTS.ELIGIBLE_CHECK, buildEligibleCheckProps({
      tiers: allTiers,
      zip: address.zip,
      availablePharmacies: expressStocks?.length || 0,
    }));

    // Only wipe express tier carts when address changes, not SHIPPING/PICKUP/PRESCRIPTION_ONLY.
    // Those should stay so user doesn't lose their selection when searching for express zipcode.
    resetExpressTierCarts?.();
  }, [setCustomerZipcode, setStockCodesInRange, setExpressStockOptions, resetExpressTierCarts, parseProducts, setProducts, setSelectedExpressTier]);

  const { onTouchStart, onTouchMove, onTouchEnd } = useTabSwipe({
    pillRef,
    isSecondTabActive: shippingMethod === constants.PICKUP,
    switchToFirst: switchToDelivery,
    switchToSecond: switchToPickup,
    isDraggingRef,
    onAfterDrag: updateTabsPill,
  });

  if (!chooseTreatmentContext) {
    return null;
  }

  const tabButtonStyle: React.CSSProperties = {
    flex: '0 0 auto',
    width: 'auto',
    minWidth: 'max-content',
    paddingLeft: '24px',
    paddingRight: '24px'
  };

  const resetSecondaryDropdown = () => {
    setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
  }

  const handleShippingMethodToggle = (newMethod: typeof constants.SHIPPING | typeof constants.EXPRESS_SHIPPING) => {
    // "Funnel Express Delivery Toggle Click" (spec §1): the express on/off toggle lives here on
    // choose-treatment (ToggleRadioButton id="express-delivery-toggle"), not on the shipping page.
    // customerZipcode presence is the available "address saved" signal on this screen.
    if (newMethod === constants.EXPRESS_SHIPPING) {
      MainTracker.track('click', SHIPPING_EVENTS.TOGGLE_CLICK, buildToggleClickProps({
        addressSaved: !!customerZipcode,
      }));
    }

    if (newMethod === constants.SHIPPING) {
      // When switching off express, clear express-related state to show all products
      // Wipe only the tier carts
      setStockCodesInRange([]);
      setExpressStockOptions([]);
      resetExpressTierCarts?.();
    }

    setPreviousShippingMethodSelected(shippingMethod);
    setShippingMethod(newMethod);
    setSelectedService(constants.SERVICE_PRESCRIPTION_AND_MEDICATION);
    resetSecondaryDropdown();
    trackShippingMethodSelected(newMethod);

    // Trigger 3: returning user (saved zip) opens express toggle — fire with available context data.
    // Tiers are accurate if user already searched this session, otherwise empty (eligible flags default false).
    if (newMethod === constants.EXPRESS_SHIPPING && customerZipcode) {
      const tiers = (expressStockOptions ?? []).flatMap((opt: any) => opt.tiers ?? []);
      MainTracker.track('custom', SHIPPING_EVENTS.ELIGIBLE_CHECK, buildEligibleCheckProps({
        tiers,
        zip: customerZipcode,
        availablePharmacies: (stockCodesInRange ?? []).length,
      }));
    }

    if (newMethod === constants.EXPRESS_SHIPPING && !customerZipcode) {
      MainTracker.track('custom', 'zipcode popup');
    }
  };

  const handlePickupServiceSelect = (service: typeof constants.SERVICE_PRESCRIPTION_ONLY | typeof constants.SERVICE_PRESCRIPTION_AND_MEDICATION) => {
    if (selectedService !== service) {
      setSelectedStockCode(null);
    }

    const sortby: SortByType = chooseTreatmentContext?.sortBy;

    if (service === constants.SERVICE_PRESCRIPTION_ONLY && sortby && [SORT_BY_PRICE_ASC, SORT_BY_PRICE_DESC].includes(sortby)) {
      chooseTreatmentContext?.setSortBy(null);
    }

    setSelectedService(service);
    setPurchaseTierFilter(service === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ? "premium" : "regular");
    setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
    setIsExpressPrescriptionReview(false);
    setNearbyPharmacies([]);
  };

  return (
    <div className='w-full flex flex-col items-center gap-4' id="shipping-and-service-selection">
      <div className='w-full flex flex-col items-center justify-between gap-3'>

        <div className="relative w-full flex flex-col items-center gap-1 rounded-md" id="service-selection-dropdown-container">
          <div
            className="shipping-method-tabs"
            style={{
              width: 'fit-content',
              maxWidth: '100%'
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              ref={pillRef}
              className="shipping-method-tabs-pill"
              aria-hidden
            />

            <button
              ref={deliveryBtnRef}
              type="button"
              style={tabButtonStyle}
              className={`shipping-method-tab-btn ${shippingMethod !== constants.PICKUP ? 'shipping-method-tab-btn--active' : ''}`}
              onClick={switchToDelivery}
            >
              {translator.translate(language, "choose_treatment", "default/delivery")}
            </button>

            <button
              ref={pickupBtnRef}
              id="self-pickup-tab-button"
              type="button"
              style={tabButtonStyle}
              className={`shipping-method-tab-btn ${shippingMethod === constants.PICKUP ? 'shipping-method-tab-btn--active' : ''}`}
              onClick={switchToPickup}
            >
              {translator.translate(language, "choose_treatment", "default/self_pickup")}
            </button>
          </div>
        </div>

        {shippingMethod !== constants.PICKUP && (
          <div className="flex items-start mt-1 gap-2">
            <MdInfoOutline className="text-[#269C6E]" />
            <span className="text-[#98A0AF] text-xs font-normal">
              {translator.translate(language, "choose_treatment", "shipping_and_service_selection/description_delivery")}
            </span>
          </div>
        )}

        {(isExpressUberFlow || isExpressUrbifyFlow) && (shippingMethod === constants.SHIPPING || shippingMethod === constants.EXPRESS_SHIPPING) && (
          <div
            id="express-delivery-combined-container"
            className={`w-full express-combined-card ${shippingMethod === constants.EXPRESS_SHIPPING ? 'express-combined-card--active' : ''}`}
          >
            <div
              id="express-delivery-toggle-container"
              className="w-full express-delivery-card"
              data-enabled={shippingMethod === constants.EXPRESS_SHIPPING ? 'true' : 'false'}
            >
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex items-center gap-2">
                  <span style={{ color: '#037D73', fontFamily: 'Roboto', fontSize: '16px', fontWeight: 700, lineHeight: '24px' }}>
                    {translator.translate(language, "choose_treatment", "shipping_and_service_selection/express_title")}
                  </span>
                  <SparkleLabel language={language} onClick={onExpressLabelClick} />
                </div>

                <span className="express-description">
                  {translator.translate(language, "choose_treatment", "shipping_and_service_selection/express_sublabel")}
                </span>
              </div>

              <div className="flex items-center ml-8 mr-2">
                <div className="relative express-toggle-wrap">
                  <ToggleRadioButton
                    id="express-delivery-toggle"
                    isToggleEnabled={shippingMethod === constants.EXPRESS_SHIPPING}
                    updateToggle={(enabled: boolean) => {
                      handleShippingMethodToggle(enabled ? constants.EXPRESS_SHIPPING : constants.SHIPPING);
                    }}
                  />

                  <div className="express-toggle-thumb" aria-hidden>
                    <svg className="express-lightning-off w-[9px] h-[13px]" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.98557 5.94348C8.96476 5.8438 8.92159 5.75105 8.8598 5.67326C8.79802 5.59547 8.71947 5.53497 8.63096 5.497L6.12765 4.42431L6.64563 0.724135C6.66464 0.591265 6.64341 0.455363 6.58506 0.336365C6.5267 0.217366 6.43429 0.121533 6.32138 0.0629229C6.20847 0.00431247 6.081 -0.0139909 5.95766 0.0106977C5.83433 0.0353862 5.72163 0.101767 5.63609 0.200104L0.15795 6.48847C0.0928327 6.56318 0.0456973 6.65378 0.0206261 6.75241C-0.00444513 6.85104 -0.00669622 6.95473 0.0140677 7.0545C0.0348316 7.15427 0.0779845 7.24711 0.139789 7.32499C0.201594 7.40286 0.280186 7.46342 0.36876 7.50142L2.87207 8.57411L2.3541 12.2759C2.33508 12.4087 2.35631 12.5446 2.41466 12.6636C2.47302 12.7826 2.56543 12.8785 2.67834 12.9371C2.79126 12.9957 2.91873 13.014 3.04206 12.9893C3.16539 12.9646 3.2781 12.8982 3.36364 12.7999L8.84177 6.51153C8.90718 6.43659 8.95447 6.34565 8.97954 6.24664C9.0046 6.14763 9.00667 6.04355 8.98557 5.94348ZM3.79553 10.4591L4.10221 8.27018C4.12181 8.13008 4.09668 7.987 4.0309 7.86412C3.96511 7.74124 3.86253 7.64575 3.73977 7.59313L1.60134 6.6766L5.20419 2.54095L4.89752 4.72982C4.87792 4.86992 4.90305 5.013 4.96883 5.13588C5.03461 5.25877 5.13719 5.35425 5.25995 5.40687L7.39838 6.3234L3.79553 10.4591Z" fill="#3C4150" />
                      <path d="M3.79553 10.4591L4.10221 8.27018C4.12181 8.13008 4.09668 7.987 4.0309 7.86412C3.96511 7.74124 3.86253 7.64575 3.73977 7.59313L1.60134 6.6766L5.20419 2.54095L4.89752 4.72982C4.87792 4.86992 4.90305 5.013 4.96883 5.13588C5.03461 5.25877 5.13719 5.35425 5.25995 5.40687L7.39838 6.3234L3.79553 10.4591Z" fill="#3C4150" />
                    </svg>
                    <svg className="express-lightning-on w-[10px] h-[13px]" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.98397 5.94348C9.96084 5.8438 9.91288 5.75105 9.84422 5.67326C9.77557 5.59547 9.6883 5.53497 9.58996 5.497L6.8085 4.42431L7.38403 0.724135C7.40516 0.591265 7.38157 0.455363 7.31673 0.336365C7.25189 0.217366 7.14921 0.121533 7.02376 0.0629229C6.8983 0.00431247 6.75666 -0.0139909 6.61963 0.0106977C6.48259 0.0353862 6.35736 0.101767 6.26232 0.200104L0.1755 6.48847C0.103147 6.56318 0.0507748 6.65378 0.0229179 6.75241C-0.00493903 6.85104 -0.00744025 6.95473 0.0156307 7.0545C0.0387017 7.15427 0.0866494 7.24711 0.155321 7.32499C0.223993 7.40286 0.311318 7.46342 0.409734 7.50142L3.19119 8.57411L2.61566 12.2759C2.59454 12.4087 2.61812 12.5446 2.68296 12.6636C2.7478 12.7826 2.85048 12.8785 2.97594 12.9371C3.10139 12.9957 3.24303 13.014 3.38007 12.9893C3.5171 12.9646 3.64233 12.8982 3.73738 12.7999L9.82419 6.51153C9.89686 6.43659 9.94941 6.34565 9.97726 6.24664C10.0051 6.14763 10.0074 6.04355 9.98397 5.94348ZM4.21726 10.4591L4.55801 8.27018C4.57978 8.13008 4.55186 7.987 4.47877 7.86412C4.40568 7.74124 4.2917 7.64575 4.1553 7.59313L1.77927 6.6766L5.78244 2.54095L5.44168 4.72982C5.41991 4.86992 5.44783 5.013 5.52092 5.13588C5.59401 5.25877 5.70799 5.35425 5.84439 5.40687L8.22042 6.3234L4.21726 10.4591Z" fill="#C1F29F" />
                      <path d="M4.21726 10.4591L4.55801 8.27018C4.57978 8.13008 4.55186 7.987 4.47877 7.86412C4.40568 7.74124 4.2917 7.64575 4.1553 7.59313L1.77927 6.6766L5.78244 2.54095L5.44168 4.72982C5.41991 4.86992 5.44783 5.013 5.52092 5.13588C5.59401 5.25877 5.70799 5.35425 5.84439 5.40687L8.22042 6.3234L4.21726 10.4591Z" fill="#C1F29F" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {shippingMethod === constants.EXPRESS_SHIPPING && (
              <ZipcodeCheck
                customerAddresses={customerAddresses || []}
                onAddressSelected={handleAddressSelected}
                country={getCart()?.locale_id || 'DE'}
              />
            )}
          </div>
        )}

        {shippingMethod === constants.PICKUP && (
          <div className="w-full flex flex-col gap-3" id="pickup-service-options">
            <div id="pickup-service-options-container">
              <button
                type="button"
                onClick={() => handlePickupServiceSelect(constants.SERVICE_PRESCRIPTION_ONLY)}
                className={`w-full text-left my-2 pickup-option-block pickup-option-block--only-prescription ${selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? 'pickup-option-block--selected' : ' cursor-pointer'}`}
                style={selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? { borderRadius: '12px', border: '1px solid #11DDAC', background: '#FFF' } : undefined}
              >
                <div className="flex flex-col gap-0 w-full">
                  <div className={`flex items-center${selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ? ' gap-3' : ''}`}>
                    <div className="flex-shrink-0">
                      {selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? <RadioSelected /> : <RadioUnselected />}
                    </div>

                    <span
                      className="block font-semibold text-[16px] font-poppins"
                      style={{ color: selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? '#037D73' : '#616975' }}
                    >
                      {translator.translate(language, "choose_treatment", "shipping_and_service_selection/only_prescription_option")}
                    </span>
                  </div>

                  {selectedService === constants.SERVICE_PRESCRIPTION_ONLY && (
                    <p className="flex text-[11px] font-poppins font-normal mt-1.5 text-[#037D73] leading-snug whitespace-break-spaces">
                      {translator.translate(language, "choose_treatment", "shipping_and_service_selection/description_prescription_only")}
                    </p>
                  )}
                </div>
              </button>

              <button
                type="button"
                onClick={() => handlePickupServiceSelect(constants.SERVICE_PRESCRIPTION_AND_MEDICATION)}
                className={`w-full mb-2 text-left overflow-hidden pickup-option-block ${selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ? 'pickup-option-block--selected pickup-option-block--pay-once-expanded' : 'pickup-option-block--pay-once-compact cursor-pointer'}`}
                style={selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ? { borderRadius: '12px', border: '1px solid #11DDAC', background: '#FFF' } : undefined}
              >
                <div className="flex flex-col py-2 gap-0 w-full">
                  <div className={`flex items-center${selectedService === constants.SERVICE_PRESCRIPTION_ONLY ? ' gap-3' : ''}`}>
                    <div className="flex-shrink-0">
                      {selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ? <RadioSelected /> : <RadioUnselected />}
                    </div>

                    <span
                      className="block font-semibold text-[16px] font-poppins"
                      style={{ color: selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION ? '#037D73' : '#616975' }}
                    >
                      {translator.translate(language, "choose_treatment", "shipping_and_service_selection/pay_once")}
                    </span>
                  </div>

                  <span className="block text-sm font-roboto text-[#98A0AF] mt-1">
                    {translator.translate(language, "choose_treatment", "shipping_and_service_selection/pay_once_subtitle")}
                  </span>

                  {selectedService === constants.SERVICE_PRESCRIPTION_AND_MEDICATION && (
                    <>
                      <p className="text-sm font-light font-poppins text-[#037D73] leading-snug my-2">
                        {translator.translate(language, "choose_treatment", "shipping_and_service_selection/description_premium")}
                      </p>

                      <ul className="list-none pl-0 mt-0 flex flex-col items-start gap-1">
                        {[1, 2, 3].map((i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckmarkBullet />
                            <span className="text-sm flex-1 font-light font-poppins text-[#037D73]">
                              {translator.translate(language, "choose_treatment", `shipping_and_service_selection/pay_once_benefit_${i}`)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </button>
            </div>

            {isFastApprovalFlow ? (
              <FastDoctorReview
                enabled={isExpressPrescriptionReview}
                onToggleChange={(enabled: boolean) => {
                  setIsExpressPrescriptionReview(enabled);
                  setSelectedApprovalService(enabled ? constants.SERVICE_FAST_APPROVAL : constants.SERVICE_STANDARD_APPROVAL);
                }}
                title={translator.translate(language, "choose_treatment", "shipping_and_service_selection/fast_doctor_review_title")}
                description={translator.translate(language, "choose_treatment", "shipping_and_service_selection/fast_doctor_review_description")}
                label={translator.translate(language, "choose_treatment", "default/label")}
                toggleId="fast-doctor-review-toggle-choose-treatment"
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingAndServiceDropdowns;